import React from "react";
import {connect} from "react-redux";
import * as jotoTypes from "../../api/joto-types";
import * as joto from "../../api/joto";
import * as jotoui from "../../joto-ui-api";
import PropTypes from 'prop-types';
import {FormGroupInput} from "../FormGroup";
import {Form} from "react-final-form";
import {generateDummyPicture, UserMessage, validateShort} from "../JotoUIUtils";
import {ProjectCard} from "../project-card/ProjectCard";
import ReactTooltip from "react-tooltip";

import * as log from 'loglevel';
import {ProjectForm} from "../project/ProjectForm";
import * as SmoothUI from "@smooth-ui/core-sc";
import * as env from "../../env";

// log.setDefaultLevel(log.levels.DEBUG);
const logger = log.getLogger('ProfilePage.tsx');
logger.setLevel(env.logLevel);

const sleep = (ms: number) => new Promise<string>(resolve => setTimeout(() => resolve('sleep-timeout'), ms));

interface PublicValues {
    nick: string, 
    description: string

}
interface PrivateValues {
    name: string, 
}
interface ProfilePageState {
    loggedIn: string | null,
    ownProjectList: jotoTypes.Project[],
    selectedPid: jotoTypes.ValidPidValues,
    profile: jotoTypes.Profile,
    ownProjectListUserMessage: UserMessage
}
interface ProfilePageProps extends ProfilePageState {
    joto: joto.Joto,
    jotoui: jotoui.JotoUIApi
}

interface ByteSizeEstimateFromImageUrl {
    width: number,
    height: number,
    size: number
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function byteSizeEstimateFromImageUrlViaPerformanceApi(pictureURL : string) : Promise<number> {
    const performanceEntries = performance.getEntriesByName(pictureURL);

    let result = -1;

    if(performanceEntries.length > 0) {
        // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/transferSize
        const performanceEntry : PerformanceEntry = performanceEntries[0];
        const perfEntry : any = performanceEntry;

        if ("decodedBodySize" in perfEntry)
            logger.debug("decodedBodySize = " + perfEntry.decodedBodySize);
        else
            logger.debug("decodedBodySize = NOT supported");

        if ("encodedBodySize" in perfEntry)
            logger.debug("encodedBodySize = " + perfEntry.encodedBodySize);
        else
            logger.debug("encodedBodySize = NOT supported");

        if ("transferSize" in perfEntry) {
            result = perfEntry.transferSize as number;
            logger.debug("transferSize = " + perfEntry.transferSize);
        } else {
            logger.debug("transferSize = NOT supported");
        }
    }
    if(!result) // protection in case in some browsers the perfEntry.transferSize is not a number or null or undefined.
        result = -1;

    return result;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function byteSizeEstimateFromImageUrlViaCanvasAndBlobApi(img : HTMLImageElement) : Promise<number> {
    let result = -1;

    // https://javascript.info/blob
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;

    const context = canvas.getContext('2d');
    let blob;
    if(context) {
        context.drawImage(img, 0, 0);
        blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.7));
        if(blob) {
            logger.debug(`blob.size: ${blob.size}`);
            result = blob.size;
        }
    }
    return result;
}

async function getImage(pictureURL : string, crossOrigin=true, timeout = 4000) : Promise<HTMLImageElement> {
    const img = new Image();
    if(crossOrigin)
        img.crossOrigin = "anonymous";
    var p = new Promise<void>((resolve, reject) => {
        const fn = () => {
            resolve();
        };
        img.onload = fn;
    });
    // Access to image at from origin has been blocked by CORS policy: No 'Access-Control-Allow-Origin' header is present on the requested resource.
    img.src = pictureURL;
    const q = sleep(timeout);
    const pq = await Promise.race([p, q]);

    if(typeof pq === 'string' && pq === 'sleep-timeout') {
        throw Error('ProfilePage.byteSizeEstimateFromImageUrl something went wrong when waiting for the image to load.');
    }
    return img;
}

// https://images.pexels.com/photos/461049/pexels-photo-461049.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=500
// http://localhost:3000/img/wefugees.jpeg
// https://picsum.photos/id/609/536/354
async function byteSizeEstimateFromImageUrl(pictureURL : string) : Promise<ByteSizeEstimateFromImageUrl> {
    if(pictureURL.length === 0)
        return {width: 0, height: 0, size:0 };

    let width = 10000;
    let height = 10000;
    let size = 1000*1000*1000*1000;
    let crossOrigin = true;
    try {
        // load image and wait for it to load:

        // image loading finished
        let img;
        try {
            // will not add additional benefits besides the image size check
            crossOrigin = false;
            size = -1; 
            img = await getImage(pictureURL, crossOrigin, 2000);
        } catch(e) {
            logger.debug('Cross Origin did not work: ' + JSON.stringify(e));
            crossOrigin = false;
            size = -1;
            img = await getImage(pictureURL, crossOrigin);
        }

        width = img.width;
        height = img.height;

        logger.debug(`image src : ${img.src}, width: ${width}, height: ${height}`);

        // does not work for images that are not hosted on our server, which is basically all images we care about :(
        // const sizeViaPerformanceApi = await byteSizeEstimateFromImageUrlViaPerformanceApi(img.src);

        if(crossOrigin) {
            // will not add additional benefits besides the image size check
            // size = await byteSizeEstimateFromImageUrlViaCanvasAndBlobApi(img);
            size = -1;
        }
        return {width, height, size};
    } catch(e) {
        logger.error(`While eximating the image size for image ${pictureURL} an error happened: ${JSON.stringify(e, null, 2)}`);
        return {width, height, size};
    }
}

class ProfilePage_ extends React.Component<ProfilePageProps, {}> {
    constructor(props: ProfilePageProps){
        super(props);

        this.publicProfileForm = this.publicProfileForm.bind(this);
        this.onSubmitPublic  = this.onSubmitPublic.bind(this);
        this.privateProfileForm = this.privateProfileForm.bind(this);
        this.onSubmitPrivate = this.onSubmitPrivate.bind(this);
        this.onClickProject  = this.onClickProject.bind(this);
        this.editOwnProjectOnSubmit = this.editOwnProjectOnSubmit.bind(this);
    }

    clearMessage = async (sleepTime: number) => {
        await sleep(sleepTime);
        this.props.jotoui.updateOwnProjectListMessage(null);
        // store.dispatch({type: 'updateOwnProjectListMessage', value: null});
    };


    publicProfileForm(handleSubmit: (values : PublicValues) => any,) {
        return (
            <Form onSubmit={handleSubmit} initialValues={{...this.props.profile}}>
                {({handleSubmit, submitting}) => {

                    let alert_class = 'alert-success d-none'; // 'alert-warning', 'alert-danger';
                    let alert_text = '&nbsp;';
                    if(submitting) {
                        alert_class = 'alert-warning';
                        alert_text = '... wird gespeichert ...'
                    } else {
                        alert_class = 'alert-success d-none'; // 'alert-warning', 'alert-danger';
                        alert_text = '&nbsp;'
                    }

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h2 className="modal-title" id="publicProfileModalLabel">Profil Informationen</h2>
                                    <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                </div>
                                <div  className="modal-body">
                                    <FormGroupInput prefix="edit-public-profile-form"
                                                    label="Spitzname"
                                                    placeholder="Spitzname"
                                                    validate={validateShort}
                                                    valuePropertyKey="nick"
                                                    />
                                    <FormGroupInput prefix="edit-public-profile-form"
                                                    label="Kurzbeschreibung"
                                                    placeholder="Kurzbeschreibung"
                                                    validate={validateShort}
                                                    valuePropertyKey="description"
                                                    />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" data-dismiss="modal">Schließen</button>
                                    <button type='submit' className="btn btn-dark">Speichern</button>
                                </div>
                                <div className={"alert mt-2 " + alert_class} role="alert">
                                    {alert_text}
                                </div>
                            </div>
                        </form>)
                }}
            </Form>
        )
    }

    privateProfileForm(handleSubmit: (values : PrivateValues) => any,) {
        return (
            <Form onSubmit={handleSubmit}  initialValues={{...this.props.profile}}>
                {({handleSubmit, submitting}) => {

                    let alert_class = 'alert-success d-none'; // 'alert-warning', 'alert-danger';
                    let alert_text = '&nbsp;';
                    if(submitting) {
                        alert_class = 'alert-warning';
                        alert_text = '... wird gespeichert ...'
                    } else {
                        alert_class = 'alert-success d-none'; // 'alert-warning', 'alert-danger';
                        alert_text = '&nbsp;'
                    }

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h2 className="modal-title" id="publicProfileModalLabel">Profil Informationen</h2>
                                    <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                </div>
                                <div className="modal-body">
                                    <FormGroupInput prefix="edit-private-profile-form"
                                                    label="Vor- und Nachname"
                                                    placeholder="Vor- und Nachname"
                                                    validate={validateShort}
                                                    valuePropertyKey="name"
                                                    />
                                    <SmoothUI.FormGroup>
                                        <SmoothUI.Label htmlFor="edit-private-profile-form-email">Email</SmoothUI.Label>
                                        <div>Dieses Feld entspricht den Logindaten und kann nicht geändert werden.</div>
                                    </SmoothUI.FormGroup>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" data-dismiss="modal">Schließen</button>
                                    <button type='submit' className="btn btn-dark">Speichern</button>
                                </div>
                                <div className={"alert mt-2 " + alert_class} role="alert">
                                    {alert_text}
                                </div>
                            </div>
                        </form>)
                }}
            </Form>

          )
    }
        
    
    onSubmitPublic = async (values : PublicValues) => {
        const {nick, description} = values;
        const name = this.props.profile.name; 
        const profile = {name, nick, description};
        try {
            const updatedProfile : jotoTypes.ProfilePublic = await this.props.joto.createOrUpdateProfile(profile);
            logger.debug('ProfileEditPage_ : calling this.props.jotoui.updateProfile() : ' + JSON.stringify(updatedProfile));
            this.props.jotoui.updateProfile(updatedProfile);
            this.props.jotoui.successMessage('Öffentliches Profil', 'Das Speichern war erfolgreich.');
            (window as any).$('#modal-public-profile').modal('hide');
        } catch(e) {
            this.props.jotoui.failedMessage('Öffentliches Profil', `Beim Speichern ist ein Fehler aufgetreten: ${JSON.stringify(e, null, 2)}.`);
            logger.error('ProfileEditPage_onSubmit : Error : ' + JSON.stringify(e) + ' Profile : ' + JSON.stringify(profile));
        }
    };

    onSubmitPrivate = async (values : PrivateValues) => {
        const {name} = values;
        const nick  = this.props.profile.nick; 
        const description = this.props.profile.description;
        const profile = {name, nick, description};
        try {
            const updatedProfile : jotoTypes.ProfilePublic = await this.props.joto.createOrUpdateProfile(profile);
            logger.debug('ProfileEditPage_ : calling this.props.jotoui.updateProfile() : ' + JSON.stringify(updatedProfile));
            this.props.jotoui.updateProfile(updatedProfile);
            this.props.jotoui.successMessage('Privates Profil', 'Das Speichern war erfolgreich.');
            (window as any).$('#modal-private-profile').modal('hide');
        } catch(e) {
            this.props.jotoui.failedMessage('Privates Profil', `Beim Speichern ist ein Fehler aufgetreten: ${JSON.stringify(e, null, 2)}.`);
            logger.error('ProfileEditPage_onSubmit : Error : ' + JSON.stringify(e) + ' Profile : ' + JSON.stringify(profile));
        }
    };

    onClickProject(p: jotoTypes.Project){
        logger.debug(`ProfilePage.tsx: onClickProject: ${JSON.stringify(p, null, 2)}`);
        this.props.jotoui.ownProjectListSelectPid(p.pid);

        (window as any).$('#profile-page-modal-project-edit').modal('show');
    }

    editOwnProjectOnSubmit = async (projectData: jotoTypes.ProjectPublic) => {
        if (!this.props.joto.userData)
            throw new Error('You are not logged in; you cannot perform this operation.');

        logger.debug('ProfilePage_editOwnProjectOnSubmit : values : ' + JSON.stringify(projectData));

        let pictureURL = ('' + projectData.pictureURL).trim();
        const sizeEstimateFromImageUrl = await byteSizeEstimateFromImageUrl(pictureURL);
        if(sizeEstimateFromImageUrl.size > 400000 || sizeEstimateFromImageUrl.width > 1000 || sizeEstimateFromImageUrl.height > 1000) {
            pictureURL = generateDummyPicture(projectData.name); // `https://dummyimage.com/287x180/F5C979/000.jpg&text=${projectData.name.trim()[0]}`;
            this.props.jotoui.failedMessage('Bild URL Validierung', 'Das Bild auf das die URL Verweist ist zu groß. Bitte verwende ein Bild das maximal 1000x1000 Pixel groß ist und nicht mehr als 400kB an Speicher benötigt.');
            projectData.pictureURL = pictureURL;
        }

        try {
            const pid = this.props.selectedPid;
            await this.props.joto.writeProject(pid, projectData);
            this.props.jotoui.successMessage(`Projekt: ${projectData.name}`, 'Das Speichern war erfolgreich.');
            (window as any).$('#profile-page-modal-project-edit').modal('hide');
        } catch (e) {
            this.props.jotoui.failedMessage(`Projekt: ${projectData.name}`, `Beim Speichern ist ein Fehler aufgetreten: ${JSON.stringify(e, null, 2)}.`);
            logger.error('ProfilePage_editOwnProjectOnSubmit : Error : ' + JSON.stringify(e) + ' Profile : ' + JSON.stringify(projectData));
        }
    };

    createNewProjectOnSubmit = async (values: jotoTypes.ProjectPublic) => {
        if (!this.props.joto.userData)
            throw new Error('You are not logged in; you cannot perform this operation.');

        logger.debug('ProjectEditPage_createNewProjectOnSubmit : values : ' + JSON.stringify(values));

        let pictureURL = ('' + values.pictureURL).trim();
        const sizeEstimateFromImageUrl = await byteSizeEstimateFromImageUrl(pictureURL);
        if(sizeEstimateFromImageUrl.size > 400000 || sizeEstimateFromImageUrl.width > 600 || sizeEstimateFromImageUrl.height > 400) {
            pictureURL = generateDummyPicture(values.name); //`https://dummyimage.com/287x180/F5C979/000.jpg&text=${values.name.trim()[0]}`;
            this.props.jotoui.failedMessage('Bild URL Validierung', 'Das Bild auf das die URL Verweist ist zu groß. Bitte verwenden Sie ein Bild das maximal 600x400 Pixel groß ist und nicht mehr als 400kB an Speicher benötigt.');
            values.pictureURL = pictureURL;
        }

        try {
            const r = await this.props.joto.createProject(values);
            logger.debug('r : ' + JSON.stringify(r));
            this.props.jotoui.successMessage(`Projekt: ${values.name}`, 'Das Speichern war erfolgreich.');
            (window as any).$('#profile-page-modal-project-create').modal('hide');
        } catch(e) {
            this.props.jotoui.failedMessage(`Projekt: ${values.name}`, `Beim Speichern ist ein Fehler aufgetreten: ${JSON.stringify(e, null, 2)}.`);
            logger.error('ProfilePage_createNewProjectOnSubmit : Error : ' + JSON.stringify(e) + ' Profile : ' + JSON.stringify(values));
        }
    };

    deleteProject = (): void => { // e: react.MouseEvent
        if (!this.props.joto.userData)
            throw new Error('You are not logged in; you cannot perform this operation.');

        const s = this.props.jotoui.store.getState(); // as jotoui.JotoUIState
        const uid = this.props.joto.userData.uid;
        const pid = s.ui.ownProjectList.pid;
        logger.debug('ProjectEditPage_deleteProject : uid:pid : ' + uid + ':' + pid);
        const ownProjectList = s.ownProjectList;
        let newPid: jotoTypes.ValidPidValues | null = null;
        for (const p of ownProjectList) {
            if (p.pid === pid)
                continue;
            newPid = p.pid;
            break;
        }
        (async () => {
            try {
                await this.props.joto.deleteProject(pid);
                this.props.jotoui.updateOwnProjectListMessage('Das Löschen war erfolgreich.');
                this.props.jotoui.successMessage(`Projekt wurde gelöscht`, 'Das Löschen des Projekts war erfolgreich.');
                if (newPid != null)
                    this.props.jotoui.ownProjectListSelectPid(newPid);
                (window as any).$('#profile-page-modal-project-edit').modal('hide');
            } catch (e) {
                this.props.jotoui.failedMessage(`Projekt löschen`, `Beim Löschen ist ein Fehler aufgetreten: ${JSON.stringify(e, null, 2)}.`);
                logger.debug('ProjectEditPage_deleteProject : Error : ' + e.toString() + ' Prorject : ' + uid + ':' + pid + '\n' + e.stack);
            }
        })();
    };

    render(){
        const {name, nick, description} = this.props.profile; 
        const ownProjectListSorted : jotoTypes.Project[] = [...this.props.ownProjectList].sort(function(a, b) {
            return b.likeCount-a.likeCount; 
        });

        let selectedProject = {};
        if (this.props.ownProjectList != null) {
            for (const p of this.props.ownProjectList) {
                if (p.pid === this.props.selectedPid) {
                    selectedProject = p;
                    break;
                }
            }
        }
        const ownProjectListUserMessage = this.props.ownProjectListUserMessage;


        return (
            <div>
                <h2 style={{marginTop: "2rem"}}>Öffentliches Profil</h2>
                <hr/>
                <div className="row">
                    <div className="col-10">
                        <div className="row">
                            <div className="col-md-3">Spitzname:</div>
                            <div className="col-7">{nick}</div>
                        </div>
                        <div className="row">
                            <div className="col-md-3">Kurzbeschreibung:</div>
                            <div className="col-7">{description}</div>
                        </div>
                    </div>
                    <div className="col-2" >
                        <button type="button" 
                            style={{ width: 40, height: 40}}  
                            className="btn btn-dark mt-2" 
                            data-toggle="modal"
                            data-target="#modal-public-profile"
                            data-tip='tooltip'
                            data-for='public-edit'>
                                <svg xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink"
                                className={'feather'}>
                                    <use xlinkHref="./svg/joto.svg#edit"/>
                                </svg>
                        </button>
                        <ReactTooltip id='public-edit' effect="solid" className={'react-tooltip-z-index'}><span>Öffentliche Einstellungen editieren</span></ReactTooltip>

                    </div>
                </div>
                <div id="modal-public-profile" className="modal" tabIndex={-1} role="dialog" aria-labelledby="publicProfileModalLabel" aria-hidden="true">
                    <div className="modal-dialog" role="document">
                        {this.publicProfileForm(this.onSubmitPublic)}
                    </div>
                </div>
                <h2 style={{marginTop: "2rem"}}> Private Einstellungen</h2>
                <hr/>
                <div className="row">
                    <div className="col-10">
                        <div className="row">
                            <div className="col-md-3">Name:</div>
                            <div className="col-7">{name}</div>
                        </div>
                        <div className="row">
                            <div className="col-md-3">E-Mail Adresse:</div>
                            <div className="col-7">{this.props.joto.profile && this.props.joto.profile.private.email}</div>
                        </div>
                    </div>
                    <div className="col-2" >
                        <button type="button" style={{ width: 40, height: 40}}  className="btn btn-dark mt-2" data-toggle="modal"
                            data-target="#modal-private-profile"
                            data-tip='tooltip'
                            data-for='private-edit'>
                            <svg xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink"
                                className={'feather'}>
                                <use xlinkHref="./svg/joto.svg#edit"/>
                            </svg>
                        </button>
                        <ReactTooltip id='private-edit' effect="solid" className={'react-tooltip-z-index'}><span>Private Einstellungen editieren</span></ReactTooltip>

                    </div> 
                </div>
                <div id="modal-private-profile" className="modal" tabIndex={-1} role="dialog" aria-labelledby="privateProfileModalLabel" aria-hidden="true">
                    <div className="modal-dialog" role="document">
                        {this.privateProfileForm(this.onSubmitPrivate)}
                    </div>
                </div>
                <h2 style={{marginTop: "3rem", marginBottom: "3rem"}}>Projektübersicht</h2>
                <hr/>
                <div id="profile-page-modal-project-edit" className="modal" tabIndex={-1} role="dialog" aria-labelledby="profileModalProjectLabel" aria-hidden="true">
                    <div className="modal-dialog" role="document">
                        {
                            (Array.isArray(this.props.ownProjectList)) && (this.props.ownProjectList.length > 0) &&
                            <ProjectForm onSubmit={this.editOwnProjectOnSubmit} title="Bearbeite ein bestehendes Projekt" initialValues={{
                                name: '',
                                abstract: '',
                                description: '',
                                pictureURL: '',
                                discussionURL: '',
                                forProfit: 'no',
                                zip_code: '',
                                city: '',
                                state: '1',
                                ...selectedProject
                            }} userMessage={ownProjectListUserMessage} onDelete={this.deleteProject} modal={true}/>
                        }
                    </div>
                </div>
                <div id="profile-page-modal-project-create" className="modal" tabIndex={-1} role="dialog">
                    <div className="modal-dialog" role="document">
                        <ProjectForm onSubmit={this.createNewProjectOnSubmit} title="Erstelle ein neues Projekt" initialValues={{
                            name: '',
                            abstract: '',
                            description: '',
                            pictureURL: '',
                            discussionURL: '',
                            forProfit: 'no',
                            zip_code: '',
                            city: '',
                            state: '1',
                        }} modal={true}/>
                    </div>
                </div>
                <div className="row">
                    <div className="col-10">
                        <div className="row">
                            {ownProjectListSorted.map((project, i) => {
                                return  (
                                    <div key={`project${i}`} className="col-lg-4" >
                                        <ProjectCard project={project} jotoui={this.props.jotoui} fnclick={this.onClickProject}/>
                                    </div>)
                            })}
                        </div>
                    </div>
                    <div className="col-2">
                        <div  data-toggle="modal" data-target="#profile-page-modal-project-create">
                            <button type="button" 
                                    style={{ width: 40, height: 40}}  
                                    className="btn btn-dark mt-2"
                                    data-tip='tooltip'
                                    data-for='plus-button' >
                                    <i className="icon icon--plus"/>
                             </button>
                        </div>
                        <ReactTooltip id='plus-button' effect="solid" className={'react-tooltip-z-index'}><span>Projekt hinzufügen</span></ReactTooltip>

                    </div>
                </div>
            </div>
        )
    }
}

// @ts-ignore
ProfilePage_.propTypes = {
    joto: PropTypes.object.isRequired,
    jotoui: PropTypes.object.isRequired,
};

const ProfilePage_mapStateToProps = (state : jotoui.JotoUIState) : ProfilePageState => ({
    profile: {...{name: '', nick: '', description: ''}, ...state.profile},
    ownProjectList: state.ownProjectList,
    selectedPid: state.ui.ownProjectList.pid,
    loggedIn: state.ui.loggedIn,
    ownProjectListUserMessage: state.ui.ownProjectList.userMessage
});

const ProfilePage = connect(ProfilePage_mapStateToProps)(ProfilePage_);

export {ProfilePage};

